import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { CONFIG_TOKEN } from 'config-token';
import { createForm, createFormControl, TypedFormArray } from 'osn-shared';
import { combineLatest, map, Observable, startWith, takeUntil, withLatestFrom } from 'rxjs';
import { RootAppState } from '../../../app.reducer';
import { selectCxbCountries, selectCxbUserSegments } from '../../../app.selectors';
import { Config } from '../../../config';
import { Country } from '../../../domain/country';
import { RouterState } from '../../../router.selectors';
import { MultiSelectOption } from '../multi-select/multi-select.component';
import { PageDetailDialogComponent } from '../page-detail-dialog/page-detail-dialog.component';
import { PageDialogVariant } from '../page-detail-dialog/page-dialog-variant';
import { RedeemPageDetailDialogDataViewModel } from './redeem-page-detail-dialog-data.view-model';
import { RedeemPageDetailDialogModalViewModel } from './redeem-page-detail-dialog-modal.view-model';

export interface RedeemDialogAdditionalData {
  userSegmentIds: string[];
  countryIds: string[];
}

@Component({
  selector: 'osn-cxb-redeem-page-detail-dialog',
  templateUrl: './redeem-page-detail-dialog.component.html',
  styleUrls: ['./redeem-page-detail-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RedeemPageDetailDialogComponent extends PageDetailDialogComponent<
  RedeemPageDetailDialogDataViewModel,
  RedeemDialogAdditionalData
> {
  public countryOptions$: Observable<MultiSelectOption[]> = this.rootStore
    .select(selectCxbCountries)
    .pipe(map((countries) => countries.map((country) => ({ id: country.id, label: country.name }))));

  public userSegmentOptions$: Observable<MultiSelectOption[]> = this.rootStore
    .select(selectCxbUserSegments)
    .pipe(map((userSegments) => userSegments.map((userSegment) => ({ id: userSegment.id, label: userSegment.name }))));

  public get redeemDialogTitle() {
    switch (this.data.variant) {
      case PageDialogVariant.NEW:
        return 'Create new partner redeem page';
      case PageDialogVariant.EDIT:
        return 'Edit partner redeem page';
      case PageDialogVariant.READ_ONLY:
        return 'View partner redeem page details';
    }
  }
  public redeemTargetUrls: string[] = [];
  public redeemPageTargetBaseUrl: string;
  private readonly currentLanguageCodes = ['en', 'ar'];

  constructor(
    @Inject(MAT_DIALOG_DATA) data: RedeemPageDetailDialogModalViewModel,
    @Inject(CONFIG_TOKEN) private readonly config: Config,
    private readonly rootStore: Store<RootAppState & RouterState>
  ) {
    super(data);
    this.redeemPageTargetBaseUrl = this.config.redeemPageTargetBaseUrl;
  }

  public onCopyClick(): void {
    const clipBoardText = this.redeemTargetUrls.reduce((clipboardText, url) => {
      return (clipboardText += `${url}\n`);
    }, '');
    navigator.clipboard.writeText(clipBoardText);
  }

  protected loadAdditionalParameters$(): Observable<RedeemDialogAdditionalData> {
    return combineLatest([this.userSegmentOptions$, this.countryOptions$]).pipe(
      map(([userSegments, countries]) => ({
        userSegmentIds: userSegments.map((userSegment) => userSegment.id),
        countryIds: countries.map((country) => country.id),
      }))
    );
  }

  protected initForm(metaTagsControl: TypedFormArray<{ key: string; value: string }>, additionalParams: RedeemDialogAdditionalData): void {
    this.formControls = {
      pageUrl: createFormControl(this.data.data?.pageUrl ?? '', [Validators.required]),
      internalName: createFormControl(this.data.data?.internalName ?? '', [Validators.required]),
      tabTitleEnglish: createFormControl(this.data.data?.tabTitleEnglish ?? '', [Validators.required]),
      tabTitleArabic: createFormControl(this.data.data?.tabTitleArabic ?? '', [Validators.required]),
      countryIds: createFormControl(this.data.data?.countryIds.length ? this.data.data?.countryIds : additionalParams.countryIds, [
        Validators.required,
      ]),
      userSegmentIds: createFormControl(
        this.data.data?.userSegmentIds.length ? this.data.data?.userSegmentIds : additionalParams.userSegmentIds,
        [Validators.required]
      ),
      metaTags: metaTagsControl,
    };

    this.form = createForm(this.formControls);

    combineLatest([
      this.rootStore.select(selectCxbCountries),
      this.form.controls.countryIds.valueChanges.pipe(startWith(additionalParams.countryIds)),
      this.form.controls.pageUrl.valueChanges.pipe(startWith(this.formControls.pageUrl.value ?? '')),
    ])
      .pipe(
        takeUntil(this.onDestroy$),
        withLatestFrom(this.countryOptions$),
        map(([[countryOptions, selectedCountryIds, pageUrl], countries]) => {
          return { selectedCountries: countries.filter((country) => selectedCountryIds.includes(country.id)), pageUrl, countryOptions };
        })
      )
      .subscribe(({ selectedCountries, pageUrl, countryOptions }) => {
        this.redeemTargetUrls = this.createRedeemPageUrlList(selectedCountries, pageUrl, countryOptions);
      });
  }

  private createRedeemPageUrlList(selectedCountries: MultiSelectOption[], pageUrl: string, countryOptions: Country[]): string[] {
    const urlList = [];
    this.currentLanguageCodes.forEach((language) =>
      selectedCountries.reduce((urls, curr) => {
        const currentCountryCode = countryOptions.find((country) => country.name === curr.label).iso2Code;
        urls.push(`${this.config.redeemPageTargetBaseUrl}/${language}-${currentCountryCode}/redeem${pageUrl ? `/${pageUrl}` : ''}`);
        return urls;
      }, urlList)
    );
    return urlList;
  }
}
